import '@material/mwc-icon-button';
import '@material/mwc-list';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-textfield';
import {
  observableValue,
  useEffect,
  useMemo,
  useObservedValue,
  useRef,
  useSharableState,
  useSharedState,
  useState,
  view,
} from '@esui/core';
import { css, html } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { MdcTextfield } from '../textfield/index.js';
import { debounce } from '../util/debounce.js';

export interface Item {
  name: string;
  id?: string;
}

export interface SelectedEventData {
  detail: {
    index: number;
  };
}

export const [Autocomplete] = view('esui-material-autocomplete', {
  Props: class {
    data: Item[] = [];
    placeholder = 'Search';
    selected = observableValue<Item | undefined>(undefined);
    disabled = false;
    onSearch?: (_: string) => void = undefined;
  },

  mapBuilder: AutocompleteBuilder => (label: string) =>
    AutocompleteBuilder().placeholder(label),

  template(props) {
    const data = useObservedValue(props.data);
    const open = useSharableState(false);
    const selected = useSharedState(props.selected);
    const [searchText, setSearchText] = useState('');

    const inputRef = useRef<MdcTextfield>();
    const [rootEl, setRootEl] = useState<Element | undefined>(undefined);
    useEffect(element => {
      setRootEl(element);
    }, []);

    const displayedValue = useMemo(() => {
      if (open.value) {
        return searchText;
      }
      return searchText || selected.value?.name || '';
    }, [searchText, open.value, selected.value]);

    function onSearchFocus() {
      open.value = true;
      props.onSearch?.(inputRef.value?.value ?? '');
    }

    const onSearchTextChange = debounce(() => {
      const text = inputRef.value?.value ?? '';
      setSearchText(text);
      props.onSearch?.(text);
    });

    function close() {
      open.value = false;
      if (selected.value) {
        setSearchText(selected.value.name);
      }
    }

    function hasFocus() {
      return !!rootEl?.shadowRoot?.activeElement;
    }

    function handleBlur() {
      setTimeout(() => {
        if (!hasFocus()) {
          close();
        }
      }, 0);
    }

    function clear() {
      setSearchText('');
      selected.value = undefined;
      props.onSearch?.('');
      if (open.value) {
        setTimeout(() => inputRef.value?.focus(), 300);
      }
    }

    function onSelected({ detail: { index } }: SelectedEventData) {
      selected.value = data[index];
      close();
    }

    const handleKeydown = useMemo(
      () =>
        ({ key }: KeyboardEvent) => {
          if (key === 'Escape') {
            close();
          }
        },
      []
    );
    useEffect(element => {
      element.addEventListener('keydown', handleKeydown);
      return element.removeEventListener('keydown', handleKeydown);
    }, []);

    return html`
      <div class="container" id="container">
        <mwc-textfield
          ${ref(inputRef)}
          id="search"
          class="input"
          placeholder="${props.placeholder}"
          outlined
          .value="${displayedValue}"
          ?disabled="${props.disabled}"
          @focus="${onSearchFocus}"
          @input="${onSearchTextChange}"
          @blur="${handleBlur}"
        >
        </mwc-textfield>
        ${selected.value
          ? html`
              <mwc-icon-button
                id="clear"
                class="clear"
                icon="clear"
                ?disabled="${props.disabled}"
                @click="${clear}"
              ></mwc-icon-button>
            `
          : ''}

        <mwc-list
          id="content"
          class="content"
          ?open="${open.value}"
          @action="${onSelected}"
        >
          ${data.map(
            item =>
              html`
                <mwc-list-item @blur="${handleBlur}" @click="${close}"
                  >${item.name}
                </mwc-list-item>
              `
          )}
        </mwc-list>
      </div>
    `;
  },

  cssTemplate: css`
    *,
    *::before,
    *::after {
      padding: 0;
      margin: 0;
      box-sizing: border-box;
    }

    .container {
      display: block;
      position: relative;
    }

    .input {
      width: 100%;
    }

    .content {
      visibility: hidden;
      opacity: 0;
      transition: all 0.1s;
      position: absolute;
      left: 0;
      right: 0;
      z-index: 100;
      background-color: var(--mdc-theme-background, #ffffff);
      color: var(--mdc-theme-text-primary-on-background, inherit);
      box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.08);
      border: solid 1px rgba(0, 0, 0, 0.08);
      border-radius: 0 0 4px 4px;
    }

    .content[open] {
      visibility: visible;
      opacity: 1;
      max-height: 15rem;
      overflow-y: auto;
    }

    .clear {
      position: absolute;
      right: 0;
      transform: translateY(8%);
      color: rgba(0, 0, 0, 0.6);
      --mdc-icon-size: 20px;
    }

    mwc-list-item {
      padding: 0 16px;
    }
  `,
});
